import { rgba } from 'polished';
import React from 'react';
import { Button as PaperButton, useTheme } from 'react-native-paper';

import type { ButtonType } from '../atoms/oe-button/oe-button';

export type UseTypeModeColorsProps = {
  type?: ButtonType;
  color?: string;
  ghost?: boolean;
} & Pick<React.ComponentProps<typeof PaperButton>, 'mode' | 'disabled'>;

const useTypeModeColors = ({
  mode,
  type = 'primary',
  ghost,
  color,
  disabled,
}: UseTypeModeColorsProps) => {
  const { colors } = useTheme();

  const labelColor = React.useMemo(() => {
    if (ghost) {
      return colors.white;
    }

    if (disabled) {
      return colors.disabled;
    }

    if (mode === 'contained') {
      return colors.onSurface;
    }

    return colors[type];
  }, [colors, mode, type, disabled, ghost]);

  const borderColor = React.useMemo(() => {
    if (disabled) {
      return colors.disabled;
    }

    if (ghost) {
      return colors.white;
    }

    if (mode === 'outlined') {
      return colors[type];
    }

    return colors.transparent;
  }, [colors, disabled, ghost, mode, type]);

  const backgroundColor = React.useMemo(() => {
    if (ghost) {
      return colors.transparent;
    }

    if (color) {
      return color;
    }

    if (mode === 'outlined' || mode === 'text') {
      return colors.transparent;
    }

    if (disabled) {
      return rgba(colors.dark3, 0.34); //This is equivalent to #cbcbcb which is the disabled color of react-native-paper's button
    }

    return colors[type];
  }, [ghost, color, mode, disabled, colors, type]);

  return {
    labelColor,
    borderColor,
    backgroundColor,
  };
};

export default useTypeModeColors;
